import { Context, context, trace } from '@opentelemetry/api'

import { INSTRUMENTATION_NAME } from './constants'

const tracer = trace.getTracer(INSTRUMENTATION_NAME)

/**
 * Starts a child span under `parentCtx` and activates it for the duration of `fn`.
 *
 * `parentCtx` must be passed explicitly because Zone propagation is reliable only
 * for the synchronous moment of entering a zone, and not for surviving across zone
 * boundaries created by nested `context.with()` calls.
 *
 * @param name Span name.
 * @param fn Async work to run inside the span's context.
 * @param parentCtx Parent context for the span.
 * @returns The resolved value of `fn`.
 */
export async function runInChildSpan<T>(name: string, fn: (ctx: Context) => Promise<T>, parentCtx: Context): Promise<T> {
    // Start the span with the provided parent context (ensuring correct parent-child relationships)
    const span = tracer.startSpan(name, {}, parentCtx)
    // Create a new context with the span set to active
    const spanCtx = trace.setSpan(parentCtx, span)

    try {
        // Activate the span's context for the duration of `fn`
        return await context.with(spanCtx, () => fn(spanCtx))
    } finally {
        span.end()
    }
}
